import { LocatedVesselRecord, VesselSearchHit } from '../shared/contracts/vessel.types';

/**
 * THE FREE IN-MEMORY VESSEL INDEX — find a hull among everything Voyage Radar
 * has already loaded, without a request and without a credit.
 *
 * VOYAGE RADAR'S, not shared, and the correction is worth stating because this
 * file spent a day in `ais/shared/models/`. The argument for putting it there
 * was that its field reads already tolerated both providers' spellings
 * (`Name`/`MMSI` and `shipName`/`mmsi`), so it "was already generic". That is
 * true and it is not the test. Every rule below is a decision about a PRODUCT
 * SURFACE — a digit query is a prefix, two characters is the floor, eight rows
 * is the cap, live outranks a frame, kept rows alone match IMO and call sign —
 * and a second provider's search would be entitled to answer every one of them
 * differently. Compare `isTrackGap` in `shared/models/track-gaps.model.ts`,
 * which states what a line between two AIS reports MEANS: that is a fact about
 * the data and it is shared.
 *
 * The half that IS shared is the shape it returns (`VesselSearchHit`) and the
 * drawer chrome it renders into. A provider that wants search writes its own
 * index against its own feed and mounts its own control (`operatorKey`); it
 * does not adopt these numbers by inheriting them.
 *
 * Pure model file: no `@angular/*`, no `leaflet`, no `this` (rule R1). It takes
 * the three lists and returns hits, which is what makes it testable at all —
 * the version this replaces could only be reached through a class that wanted
 * a Leaflet map and a sensor document.
 */

/**
 * Everything loaded this session, matched on MMSI (prefix for a digit query,
 * so a part-typed number narrows) and name (case-insensitive substring).
 *
 * Those are the two fields an AIS bbox feed actually populates — name only
 * about half the time — which is why IMO and call sign are matched only
 * against kept vessels below.
 *
 * PRECEDENCE, and it matters twice: a LIVE hit always outranks a frame hit for
 * the same hull (the live one is where the vessel is NOW), and a kept record
 * never overwrites a live one either. What a kept entry does add to an
 * already-found hull is the `kept` flag, which is why the flag is computed for
 * every hit rather than only for the kept ones.
 *
 * Plain arrays rather than an interface of thunks. The thunks bought laziness
 * for the sub-two-character case, which the one caller now short-circuits
 * before it builds any of this; the interface bought a provider seam that had
 * exactly one implementer and no second candidate — the shape D8 names as the
 * trap. An inline parameter object is not that: it names three lists for the
 * reader and commits to nothing.
 */
export function searchLoadedVessels(
  sources: {
    /**
     * The vessels drawn right now, as raw GeoJSON features or plain objects —
     * `properties` is unwrapped when present.
     */
    live: any[];
    /** Earlier frames loaded this session, each with the instant it describes. */
    frames: Array<{ time: string; vessels: any[] }>;
    /**
     * Vessels the user has KEPT, already normalised (the payload envelope is
     * the caller's business — see `vrToLocatedRecord`).
     *
     * The only entries matched on IMO and call sign, because those fields come
     * from a paid identity call and bbox feeds do not carry them. They are also
     * findable with NOTHING drawn — the empty-map case.
     */
    kept?: LocatedVesselRecord[];
  },
  query: string,
  limit = 8,
): VesselSearchHit[] {
  const q = String(query ?? '').trim().toLowerCase();
  if (q.length < 2) return [];
  const digits = /^\d+$/.test(q);
  const hits = new Map<string, VesselSearchHit>();

  const match = (mmsi: string, name: string | null, extra?: string | null): boolean => {
    if (digits) return mmsi.startsWith(q);
    const inName = !!name && name.toLowerCase().includes(q);
    const inExtra = !!extra && extra.toLowerCase().includes(q);
    return inName || inExtra;
  };

  const kept: LocatedVesselRecord[] = sources.kept ?? [];
  // Derived from the NORMALISED records rather than the raw rows the provider
  // holds. Stated because it is a (tiny) difference from the pre-extraction
  // code, which built this set from raw `mmsi` fields: normalising trims and
  // honours the provider's own id fallback, so the two can only disagree about
  // a malformed row — and where they do, this one is right.
  const keptMmsis = new Set(kept.map((r) => r.mmsi));

  const consider = (p: any, source: 'live' | 'frame', frameTime?: string): void => {
    const mmsi = String(p?.['MMSI'] ?? p?.mmsi ?? '');
    if (!mmsi) return;
    const existing = hits.get(mmsi);
    // A live hit always outranks a frame hit for the same hull.
    if (existing && existing.source === 'live') return;
    const name = (p?.['Name'] ?? p?.name ?? p?.shipName ?? '') || null;
    if (!match(mmsi, name)) return;
    const lat = Number(p?.['Lat'] ?? p?.lat ?? p?.latitude);
    const lng = Number(p?.['Lng'] ?? p?.lng ?? p?.longitude);
    hits.set(mmsi, {
      mmsi,
      name,
      shipType: (p?.['Ship Type'] ?? p?.shipType ?? '') || null,
      lat: Number.isFinite(lat) ? lat : null,
      lng: Number.isFinite(lng) ? lng : null,
      source,
      frameTime: source === 'frame' ? frameTime ?? null : null,
      kept: keptMmsis.has(mmsi),
    });
  };

  for (const f of sources.live) consider(f?.properties ?? f, 'live');
  for (const step of sources.frames) {
    for (const f of step.vessels ?? []) {
      consider(f?.properties ?? f, 'frame', String(step.time ?? ''));
    }
  }
  // Kept vessels, straight off the provider's saved list — NOT off the drawn
  // markers, because a saved vessel is list-only until it is located. Their
  // identity came from a paid call, so IMO and call sign match here.
  for (const r of kept) {
    if (hits.get(r.mmsi)?.source === 'live') continue;
    const extra = [r.imoNumber, r.callSign].filter(Boolean).join(' ');
    if (!match(r.mmsi, r.name, extra)) continue;
    hits.set(r.mmsi, {
      mmsi: r.mmsi,
      name: r.name,
      shipType: r.shipType,
      lat: r.lat,
      lng: r.lng,
      source: hits.get(r.mmsi)?.source ?? 'kept',
      frameTime: null,
      kept: true,
    });
  }
  return Array.from(hits.values()).slice(0, limit);
}

/**
 * How many drawn hulls broadcast no name.
 *
 * The honest empty state: "nothing matched" and "nothing matched, and 340 of
 * the hulls out there are not saying who they are" are different answers, and
 * only the second one tells the user their query was fine.
 *
 * `shipName` is read here and was NOT read by the pre-extraction version —
 * a deliberate correction rather than an oversight carried forward. The index
 * above has always accepted `shipName` as a name, so the old count could call
 * a hull unnamed that the search could find BY name. Inert for Voyage Radar's
 * own GeoJSON, whose properties use `Name`; kept because the read costs
 * nothing and the disagreement it prevents is invisible when it happens.
 */
export function countUnnamedVessels(live: any[]): number {
  let n = 0;
  for (const f of live) {
    const p = f?.properties ?? f ?? {};
    if (!(p['Name'] ?? p.name ?? p.shipName)) n += 1;
  }
  return n;
}
